import { HorizontalContainer } from '@/components';
import { DownloadIcon, FileIcon } from '@/components/Icons';
import { LinkButton } from '@/components/buttons/LinkButton';

import { R } from '@/caregiverSpace/resources/Texts.resx';
import { getFileExtension } from '@/lib/getFileExtension';

type OtherFileViewerProps = {
    fileName: string;
    fileSizeBytes: number;
    downloadUrl: string;
};

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const OtherFileViewer = ({
    fileName,
    fileSizeBytes,
    downloadUrl,
}: OtherFileViewerProps) => {
    const extension = getFileExtension(fileName);

    return (
        <div className="w-full max-w-md rounded-xl bg-(--color-bg-canvas) p-6 shadow-lg">
            {/* Header: icon + file info */}
            <HorizontalContainer
                spacing="none"
                className="mb-4 items-start gap-3"
            >
                <FileIcon
                    variant="default"
                    className="mt-0.5 h-8 w-8 shrink-0"
                />
                <div className="min-w-0 flex-1">
                    <p className="m-0 truncate font-semibold text-(--color-text-heading)">
                        {fileName}
                    </p>
                    <HorizontalContainer spacing="md" className="mt-2">
                        {extension && (
                            <div>
                                <p className="m-0 text-xs text-(--color-text-muted)">
                                    {R.FileType}
                                </p>
                                <p className="m-0 text-sm text-(--color-text-secondary)">
                                    {extension.toUpperCase()}
                                </p>
                            </div>
                        )}
                        {fileSizeBytes > 0 && (
                            <div>
                                <p className="m-0 text-xs text-(--color-text-muted)">
                                    {R.FileSize}
                                </p>
                                <p className="m-0 text-sm text-(--color-text-secondary)">
                                    {formatFileSize(fileSizeBytes)}
                                </p>
                            </div>
                        )}
                    </HorizontalContainer>
                </div>
            </HorizontalContainer>

            <hr className="my-4 border-(--color-border-muted)" />

            {/* Body: unavailable message + download */}
            <p className="m-0 mb-2 text-sm text-(--color-text-primary)">
                {R.PreviewNotAvailable}
            </p>
            <p className="text-muted m-0 mb-5 text-sm">{R.DownloadToView}</p>

            <HorizontalContainer spacing="none" className="justify-end">
                <LinkButton
                    href={downloadUrl}
                    download={fileName}
                    variant="secondary"
                    className="inline-flex items-center gap-2"
                >
                    <DownloadIcon className="h-5 w-5 shrink-0" />
                    {R.Download}
                </LinkButton>
            </HorizontalContainer>
        </div>
    );
};
